/**
 * Strategy Call deposit — the client's public payment page.
 *
 * Where the "Place the $5,000 refundable deposit" link in our email lands.
 * Public and token-based: the client is not an HQ user.
 *
 * WHY THIS PAGE EXISTS INSTEAD OF A STRAIGHT STRIPE REDIRECT
 * The 3% is a convenience fee for choosing a card, not a deduction from the
 * deposit — so it has to be visible next to the free wire option BEFORE the
 * client commits to paying it. A redirect that landed them on a $5,150 Stripe
 * page having only ever been promised $5,000 would be the wrong way round.
 *
 * The bank details come from lib/wireInstructions, the one definition every
 * client-facing payment surface in HQ shares, so they can never drift.
 *
 * Chrome: `position:fixed; inset:0; zIndex:9999` over its own background,
 * matching DeliveryClient — index.html's app chrome otherwise colour-blocks
 * every public client page.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { WIRE_INSTRUCTIONS, WIRE_ROWS } from "@/lib/wireInstructions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (n) =>
  "$" + (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// ── Icons ─────────────────────────────────────────────────────────────────
const BankIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M4 21V10m16 11V10M2 10l10-6 10 6M9 21v-6h6v6" />
  </svg>
);
const CardIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);
const CheckCircle = ({ size = 44 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#2E7D55" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);
const ClockIcon = ({ size = 44 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#8A6D3B" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 7 12 12 15.5 14" />
  </svg>
);

function Logo() {
  return (
    <div style={{ textAlign: "center", marginBottom: 26 }}>
      <p style={{ margin: 0, fontSize: 19, fontWeight: 700, letterSpacing: "-0.4px", lineHeight: 1.05 }}>
        THE RENDER
      </p>
      <p style={{ margin: 0, fontSize: 19, fontWeight: 700, letterSpacing: "-0.4px", lineHeight: 1.05 }}>
        PROS
      </p>
    </div>
  );
}

export default function StrategyCallDeposit() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [choice, setChoice] = useState(null); // "wire" | "card"
  const [declaring, setDeclaring] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/strategy-call/${token}/public`);
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error("Could not load your deposit details");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Coming back from a cancelled Stripe checkout should explain itself rather
  // than silently showing the same two buttons again.
  useEffect(() => {
    if (searchParams.get("stripe_cancelled")) {
      toast.info("Payment cancelled — nothing has been charged.");
    }
  }, [searchParams]);

  const payByCard = () => {
    // A full navigation, not fetch: the backend answers 302 to Stripe.
    window.location.href = `${API}/strategy-call/${token}/pay`;
  };

  const declareWire = async () => {
    setDeclaring(true);
    try {
      await axios.post(`${API}/strategy-call/${token}/wire-intent`, {});
      toast.success("Thank you — we'll confirm as soon as it arrives.");
      await load();
    } catch {
      // The bank details are already on screen, so a failed ping is a courtesy
      // we lost, not a blocker for the client.
      toast.error("We couldn't notify the team — please email us the confirmation.");
    } finally {
      setDeclaring(false);
    }
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <Logo />
          <p style={{ ...s.muted, textAlign: "center" }}>Loading your deposit details…</p>
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <Logo />
          <h1 style={s.h1}>Link not found</h1>
          <p style={s.body}>
            This link may have expired or been replaced. Reply to the email we sent
            you and we'll send a new one.
          </p>
        </div>
      </div>
    );
  }

  const paid = data.payment_status === "paid";
  const awaitingWire = data.payment_status === "awaiting_wire" && !paid;

  if (paid) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, textAlign: "center" }}>
          <Logo />
          <div style={{ marginBottom: 16 }}><CheckCircle /></div>
          <h1 style={s.h1}>Deposit received</h1>
          <p style={s.body}>
            Thank you — your {fmtUSD(data.amount)} refundable deposit is paid
            {data.payment_method === "wire" ? " by wire" : ""}. We'll be in touch
            to schedule your Strategy Call.
          </p>
          {!data.signed && (
            <p style={{ ...s.muted, marginTop: 18 }}>
              One thing outstanding: the addendum still needs your signature. The
              link is in the email we sent you, or{" "}
              <a href={`${API}/strategy-call/${token}/sign`} style={s.link}>sign it now</a>.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={s.page} data-testid="strategy-call-deposit-page">
      <div style={s.card}>
        <Logo />
        <div style={s.badgeRow}>
          <span style={s.badge}>Strategy Call</span>
        </div>
        <h1 style={s.h1}>
          {fmtUSD(data.amount)} refundable deposit
        </h1>
        {data.project_name ? (
          <p style={{ ...s.muted, textAlign: "center", marginTop: -4 }}>
            {data.project_name}
          </p>
        ) : null}

        <p style={{ ...s.body, marginTop: 20 }}>
          This deposit is 100% refundable. If you decide within 24 hours after the
          call not to proceed, we return the full {fmtUSD(data.amount)} — no
          cancellation fee, no penalty, and you don't need to give us a reason.
        </p>

        {awaitingWire && (
          <div style={s.notice} data-testid="strategy-call-awaiting-wire">
            <div style={{ flexShrink: 0, marginTop: 1 }}><ClockIcon size={20} /></div>
            <div>
              <p style={{ margin: 0, fontSize: 14, color: "#5C4A25" }}>
                You've told us a wire is on its way. We'll confirm by email as soon
                as it lands — nothing else is needed from you.
              </p>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#8A7A55" }}>
                Changed your mind? You can still pay by card below.
              </p>
            </div>
          </div>
        )}

        <p style={s.sectionLabel}>Choose how to pay</p>

        {/* Wire first, deliberately: it is the option with no fee. */}
        <button
          type="button"
          onClick={() => setChoice(choice === "wire" ? null : "wire")}
          style={{ ...s.option, ...(choice === "wire" ? s.optionActive : null) }}
          data-testid="strategy-call-choose-wire"
        >
          <span style={s.optionIcon}><BankIcon /></span>
          <span style={{ flex: 1, textAlign: "left" }}>
            <span style={s.optionTitle}>Bank wire / ACH</span>
            <span style={s.optionSub}>No fee — recommended</span>
          </span>
          <span style={s.optionAmount}>{fmtUSD(data.amount)}</span>
        </button>

        {choice === "wire" && (
          <div style={s.wirePanel} data-testid="strategy-call-wire-details">
            {WIRE_ROWS.map(([label, value]) => (
              <div key={label} style={s.wireRow}>
                <span style={s.wireLabel}>{label}</span>
                <span style={s.wireValue}>{value}</span>
              </div>
            ))}
            <div style={{ ...s.wireRow, borderBottom: "none" }}>
              <span style={s.wireLabel}>Reference</span>
              <span style={s.wireValue}>
                Strategy Call — {data.client_name}
              </span>
            </div>
            <p style={{ ...s.muted, marginTop: 14 }}>
              Wires usually land the same or next business day. Questions:{" "}
              <a href={`mailto:${WIRE_INSTRUCTIONS.contact}`} style={s.link}>
                {WIRE_INSTRUCTIONS.contact}
              </a>
            </p>
            {!awaitingWire && (
              <button
                type="button"
                onClick={declareWire}
                disabled={declaring}
                style={{ ...s.primaryBtn, marginTop: 14, opacity: declaring ? 0.6 : 1 }}
                data-testid="strategy-call-declare-wire"
              >
                {declaring ? "Letting the team know…" : "I've sent the wire"}
              </button>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => setChoice(choice === "card" ? null : "card")}
          style={{ ...s.option, ...(choice === "card" ? s.optionActive : null) }}
          data-testid="strategy-call-choose-card"
        >
          <span style={s.optionIcon}><CardIcon /></span>
          <span style={{ flex: 1, textAlign: "left" }}>
            <span style={s.optionTitle}>Credit or debit card</span>
            <span style={s.optionSub}>
              Includes a {Math.round((data.card_fee_pct || 0.03) * 100)}% convenience
              fee ({fmtUSD(data.card_fee)})
            </span>
          </span>
          <span style={s.optionAmount}>{fmtUSD(data.card_amount)}</span>
        </button>

        {choice === "card" && (
          <div style={s.wirePanel} data-testid="strategy-call-card-panel">
            <p style={{ margin: 0, fontSize: 14, color: "#3A3A3A", lineHeight: 1.6 }}>
              You'll be charged {fmtUSD(data.card_amount)} — the{" "}
              {fmtUSD(data.amount)} refundable deposit plus a{" "}
              {fmtUSD(data.card_fee)} card convenience fee. Choose the bank wire
              above to pay {fmtUSD(data.amount)} with no fee.
            </p>
            <button
              type="button"
              onClick={payByCard}
              style={{ ...s.primaryBtn, marginTop: 14 }}
              data-testid="strategy-call-pay-card"
            >
              Continue to secure payment
            </button>
            <p style={{ ...s.muted, marginTop: 10, textAlign: "center" }}>
              Payments are processed by Stripe. We never see your card details.
            </p>
          </div>
        )}

        {!data.signed && (
          <p style={{ ...s.muted, marginTop: 22, textAlign: "center" }}>
            The addendum also needs your signature —{" "}
            <a href={`${API}/strategy-call/${token}/sign`} style={s.link}>
              review and sign it here
            </a>
            .
          </p>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────
const s = {
  page: {
    position: "fixed",
    inset: 0,
    overflowY: "auto",
    background: "#F4F2EE",
    fontFamily: "var(--font-sans)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "52px 16px 48px",
    zIndex: 9999,
    color: "#111",
  },
  card: {
    background: "#fff",
    borderRadius: 24,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 12px 40px rgba(0,0,0,0.08)",
    padding: "44px 44px 40px",
    maxWidth: 560,
    width: "100%",
  },
  badgeRow: { display: "flex", justifyContent: "center", marginBottom: 14 },
  badge: {
    display: "inline-block",
    padding: "5px 14px",
    background: "#F4F2EE",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 450,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: "#6B6B6B",
  },
  h1: {
    margin: "0 0 8px",
    fontSize: 27,
    fontWeight: 300,
    letterSpacing: "-0.7px",
    textAlign: "center",
    lineHeight: 1.2,
  },
  body: { margin: "0 0 8px", fontSize: 15, lineHeight: 1.65, color: "#3A3A3A" },
  muted: { margin: 0, fontSize: 13, lineHeight: 1.6, color: "#8A8783" },
  link: { color: "#111", textDecoration: "underline" },
  sectionLabel: {
    margin: "26px 0 12px",
    fontSize: 11,
    fontWeight: 450,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: "#6B6B6B",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    width: "100%",
    padding: "16px 18px",
    marginBottom: 10,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.14)",
    borderRadius: 14,
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
  },
  optionActive: {
    borderColor: "#111",
    boxShadow: "0 0 0 1px #111",
  },
  optionIcon: { display: "flex", color: "#111", flexShrink: 0 },
  optionTitle: { display: "block", fontSize: 15, color: "#111" },
  optionSub: { display: "block", fontSize: 12.5, color: "#8A8783", marginTop: 2 },
  optionAmount: {
    fontSize: 16,
    color: "#111",
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  wirePanel: {
    padding: "18px 18px 20px",
    margin: "-2px 0 14px",
    background: "#FAF9F7",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 14,
  },
  wireRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    padding: "8px 0",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    fontSize: 13.5,
  },
  wireLabel: { color: "#8A8783", flexShrink: 0 },
  wireValue: { color: "#111", textAlign: "right", wordBreak: "break-word" },
  primaryBtn: {
    display: "block",
    width: "100%",
    padding: "13px 20px",
    background: "#111",
    color: "#fff",
    border: "none",
    borderRadius: 999,
    fontSize: 15,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  notice: {
    display: "flex",
    gap: 11,
    padding: "14px 16px",
    marginTop: 18,
    background: "#FDF8EC",
    border: "1px solid rgba(138,109,59,0.28)",
    borderRadius: 12,
  },
};
